// DJSEM Prompt 115 — DistroKid Release Bridge (readiness + manual pack + future API boundary).
//
// Hard rules (from the spec):
//   • No claim of DistroKid remote upload unless official API access is verified;
//   • No credential/login storage or browser automation;
//   • No submission without rights confirmation;
//   • AI-generated assets are NOT distribution-ready without provenance/review.
//
// Default behaviour: readiness check + manual submission pack manifest + a future-API adapter
// boundary that reports `manual_only` capability. Pure-logic, unit-testable, no I/O.

// ---- contracts -----------------------------------------------------------------------------------

/** A request to run one of the DistroKid bridge modes (readiness check, manual pack, or future-API dry run). */
export interface DJSEMDistroKidReleaseBridgeRequest {
  id: string;
  organisationId?: string;
  projectId?: string;
  releaseId: string;
  mode: "readiness_check" | "manual_submission_pack" | "future_api_dry_run";
  createdBy?: string;
  createdAt: string;
}

/** A DistroKid metadata field checked for release readiness. */
export type DistroKidFieldName =
  | "artist_name" | "release_title" | "track_title" | "primary_genre" | "secondary_genre"
  | "language" | "explicit" | "release_date" | "label_name" | "credits" | "lyrics"
  | "isrc" | "upc" | "cover_song_status";

/** The readiness status of a single DistroKid metadata field. */
export interface DJSEMDistroKidFieldCheck {
  field: DistroKidFieldName;
  status: "present" | "missing" | "warning" | "not_required";
  value?: string;
  message?: string;
}

/** The readiness status of a single required DistroKid asset (audio, artwork, lyrics, credits). */
export interface DJSEMDistroKidAssetCheck {
  asset: "audio_master" | "cover_artwork" | "lyrics_doc" | "credits_doc" | "platform_asset";
  status: "present" | "missing" | "warning" | "not_required";
  ref?: string;
  message?: string;
}

/** The computed submission-readiness report for a release, with score, field/asset checks, and blockers. */
export interface DJSEMDistroKidReadinessReport {
  id: string;
  releaseId: string;
  score: number;                       // 0..100
  status: "ready" | "ready_with_warnings" | "blocked";
  requiredFields: DJSEMDistroKidFieldCheck[];
  assetChecks: DJSEMDistroKidAssetCheck[];
  rightsChecks: string[];
  warnings: string[];
  blockers: string[];
  createdAt: string;
}

/** The manifest of files composing a DistroKid manual submission pack. */
export interface DJSEMDistroKidManualSubmissionPack {
  id: string;
  releaseId: string;
  manifestRef: string;
  zipObjectRef?: string;
  files: Array<{
    label: string;
    objectRef: string;
    purpose: "audio" | "artwork" | "metadata" | "lyrics" | "credits" | "platform_asset" | "checklist";
  }>;
  warnings: string[];
  createdAt: string;
}

/** Honest capability flags a distribution provider adapter must declare (nothing defaults to supported). */
export interface DJSEMDistributionProviderCapabilities {
  provider: string;
  canUploadRelease: boolean;
  canCreateDraftRelease: boolean;
  canReadReleaseStatus: boolean;
  canSubmitRelease: boolean;
  supportsManualPack: boolean;
  status: "manual_only" | "api_supported" | "unsupported" | "unknown";
  warnings: string[];
}

// ---- inputs used to drive checks -----------------------------------------------------------------

/** Input for {@link createDistroKidReadinessReport}: a release's metadata, tracks, and rights/AI-asset state. */
export interface DistroKidReleaseInput {
  releaseId: string;
  artistName?: string;
  releaseTitle?: string;
  primaryGenre?: string;
  language?: string;
  releaseDate?: string;
  explicit?: boolean;
  labelName?: string;
  tracks: Array<{
    title?: string;
    isrc?: string;
    lyrics?: string;
    credits?: string;
    coverSong?: boolean;
    audioMasterRef?: string;
    explicit?: boolean;
  }>;
  upc?: string;
  coverArtworkRef?: string;
  rightsConfirmed?: boolean;
  aiAssetIds?: string[];
  aiAssetProvenanceOk?: boolean;         // Prompt 65 rights ledger says all AI assets have provenance + rights.
}

// ---- readiness service ---------------------------------------------------------------------------

/**
 * Score a release for DistroKid submission readiness. Blocking issues (missing artist/title/audio/
 * artwork/rights) drop the status to "blocked"; soft issues (missing lyrics, ISRC, credits) drop
 * to "ready_with_warnings". Everything present = "ready".
 */
export function createDistroKidReadinessReport(input: DistroKidReleaseInput, now = new Date().toISOString()): DJSEMDistroKidReadinessReport {
  const fields: DJSEMDistroKidFieldCheck[] = [];
  const assets: DJSEMDistroKidAssetCheck[] = [];
  const rights: string[] = [];
  const warnings: string[] = [];
  const blockers: string[] = [];

  const req = (field: DistroKidFieldName, present: boolean, value?: string, message?: string) => {
    fields.push({ field, status: present ? "present" : "missing", value, message });
    if (!present) blockers.push(`missing ${field}`);
  };
  const soft = (field: DistroKidFieldName, present: boolean, value?: string) => {
    fields.push({ field, status: present ? "present" : "warning", value, message: present ? undefined : `${field} not set (recommended)` });
    if (!present) warnings.push(`recommended: set ${field}`);
  };

  // Required release-level fields.
  req("artist_name", !!input.artistName, input.artistName);
  req("release_title", !!input.releaseTitle, input.releaseTitle);
  req("primary_genre", !!input.primaryGenre, input.primaryGenre);
  req("language", !!input.language, input.language);
  req("release_date", !!input.releaseDate, input.releaseDate);
  // Explicit is a required declaration but the value is a boolean, so we require it to be defined.
  fields.push({ field: "explicit", status: input.explicit === undefined ? "missing" : "present", value: String(input.explicit ?? "") });
  if (input.explicit === undefined) blockers.push("explicit flag not declared");

  // Track-level fields.
  const trackTitleMissing = input.tracks.some((t) => !t.title);
  fields.push({ field: "track_title", status: trackTitleMissing ? "missing" : "present" });
  if (trackTitleMissing) blockers.push("one or more tracks missing title");

  // Recommended.
  soft("label_name", !!input.labelName, input.labelName);
  soft("secondary_genre", true);   // optional field, always considered fine
  soft("upc", !!input.upc, input.upc);
  soft("isrc", input.tracks.every((t) => !!t.isrc));
  soft("lyrics", input.tracks.every((t) => !!t.lyrics));
  soft("credits", input.tracks.every((t) => !!t.credits));

  // Cover song status must be declared (present or explicitly false).
  fields.push({ field: "cover_song_status", status: "present", value: input.tracks.some((t) => t.coverSong) ? "one_or_more_covers" : "no_covers" });

  // Asset checks.
  const audioMissing = input.tracks.some((t) => !t.audioMasterRef);
  assets.push({ asset: "audio_master", status: audioMissing ? "missing" : "present" });
  if (audioMissing) blockers.push("audio master missing on one or more tracks");
  assets.push({ asset: "cover_artwork", status: input.coverArtworkRef ? "present" : "missing", ref: input.coverArtworkRef });
  if (!input.coverArtworkRef) blockers.push("cover artwork missing");
  assets.push({ asset: "lyrics_doc", status: input.tracks.every((t) => !!t.lyrics) ? "present" : "warning" });
  assets.push({ asset: "credits_doc", status: input.tracks.every((t) => !!t.credits) ? "present" : "warning" });
  // Platform assets aren't required but if AI assets are attached they must have provenance.
  if (input.aiAssetIds && input.aiAssetIds.length > 0) {
    assets.push({ asset: "platform_asset", status: input.aiAssetProvenanceOk ? "present" : "warning", message: input.aiAssetProvenanceOk ? undefined : "AI-generated assets attached without confirmed provenance" });
    if (!input.aiAssetProvenanceOk) blockers.push("AI assets attached but rights/provenance not confirmed");
  }

  // Rights.
  if (input.rightsConfirmed) rights.push("rights_confirmed_by_user");
  else blockers.push("rights not confirmed by user");

  // Score.
  const totalChecks = fields.length + assets.length + 1 /* rights */;
  const good = fields.filter((f) => f.status === "present").length + assets.filter((a) => a.status === "present").length + (input.rightsConfirmed ? 1 : 0);
  const score = Math.round((good / totalChecks) * 100);

  const status: DJSEMDistroKidReadinessReport["status"] =
    blockers.length > 0 ? "blocked" : warnings.length > 0 ? "ready_with_warnings" : "ready";

  return {
    id: `dkrr_${input.releaseId}_${Date.parse(now) || 0}`,
    releaseId: input.releaseId,
    score, status, requiredFields: fields, assetChecks: assets, rightsChecks: rights,
    warnings, blockers, createdAt: now,
  };
}

// ---- manual submission pack ---------------------------------------------------------------------

/** Input for {@link createDistroKidManualSubmissionPack}. */
export interface CreateManualPackInput {
  releaseId: string;
  audioRefs: string[];               // storage object refs
  artworkRef?: string;
  metadataJsonRef: string;
  lyricsRef?: string;
  creditsRef?: string;
  platformAssetRefs?: string[];      // approved assets only
  checklistRef: string;
}

/**
 * Build the manifest describing every file that goes into the DistroKid manual pack ZIP. No I/O:
 * callers do the actual ZIP write. Warnings are emitted for missing recommended files so the pack
 * is honest about gaps.
 */
export function createDistroKidManualSubmissionPack(input: CreateManualPackInput, now = new Date().toISOString()): DJSEMDistroKidManualSubmissionPack {
  const warnings: string[] = [];
  const files: DJSEMDistroKidManualSubmissionPack["files"] = [];

  input.audioRefs.forEach((ref, i) => files.push({ label: `audio_${i + 1}`, objectRef: ref, purpose: "audio" }));
  if (input.audioRefs.length === 0) warnings.push("no audio masters attached to pack");

  if (input.artworkRef) files.push({ label: "cover_artwork", objectRef: input.artworkRef, purpose: "artwork" });
  else warnings.push("cover artwork missing from pack");

  files.push({ label: "metadata.json", objectRef: input.metadataJsonRef, purpose: "metadata" });
  if (input.lyricsRef) files.push({ label: "lyrics.md", objectRef: input.lyricsRef, purpose: "lyrics" });
  if (input.creditsRef) files.push({ label: "credits.md", objectRef: input.creditsRef, purpose: "credits" });
  (input.platformAssetRefs ?? []).forEach((ref, i) => files.push({ label: `platform_asset_${i + 1}`, objectRef: ref, purpose: "platform_asset" }));
  files.push({ label: "checklist.md", objectRef: input.checklistRef, purpose: "checklist" });

  warnings.push("This pack is not legal advice. Confirm rights, cover-song licences, and platform-specific requirements before submitting to any distributor.");

  return {
    id: `dkpack_${input.releaseId}_${Date.parse(now) || 0}`,
    releaseId: input.releaseId,
    manifestRef: `manifest:${input.releaseId}`,
    files,
    warnings,
    createdAt: now,
  };
}

// ---- future API adapter boundary ----------------------------------------------------------------

/**
 * DistroKid provider adapter. Default (manual, no official write API) reports `manual_only`; a
 * future real adapter can replace this to report `api_supported`. Frontend must not offer upload
 * buttons unless `canUploadRelease` is true.
 */
export interface DJSEMDistributionProviderAdapter {
  provider: "distrokid" | "manual" | "future";
  getCapabilities(): DJSEMDistributionProviderCapabilities;
}

/** The default (honest, manual-only) DistroKid capabilities — no automated upload is offered. */
export const DEFAULT_DISTROKID_CAPABILITIES: DJSEMDistributionProviderCapabilities = {
  provider: "distrokid",
  canUploadRelease: false,
  canCreateDraftRelease: false,
  canReadReleaseStatus: false,
  canSubmitRelease: false,
  supportsManualPack: true,
  status: "manual_only",
  warnings: [
    "DistroKid publishing is not automated by DJSEM. Use the manual submission pack.",
    "Enable a future adapter only when official DistroKid API access is verified.",
  ],
};

/** The default DistroKid adapter: always reports manual-only capabilities (no upload API). */
export class DistroKidManualBridgeAdapter implements DJSEMDistributionProviderAdapter {
  provider = "distrokid" as const;
  /**
   * Get this adapter's declared capabilities.
   * @returns the default manual-only DistroKid capabilities.
   */
  getCapabilities(): DJSEMDistributionProviderCapabilities { return DEFAULT_DISTROKID_CAPABILITIES; }
}
